import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, DollarSign,
  TrendingUp, FileText,
  CreditCard, Percent, AlertCircle, CheckCircle, Clock,
  ArrowLeft, Loader2, BarChart3,
  Download,
  Filter, 
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


const COLORS = [
  "#3b82f6",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
];

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-400",
    green:
      "from-green-500/10 to-green-600/5 border-green-500/20 text-green-400",
    purple:
      "from-purple-500/10 to-purple-600/5 border-purple-500/20 text-purple-400",
    orange:
      "from-orange-500/10 to-orange-600/5 border-orange-500/20 text-orange-400",
    red: "from-red-500/10 to-red-600/5 border-red-500/20 text-red-400",
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses[color]} rounded-2xl p-6 border shadow-lg hover:shadow-xl transition-all`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${color}-500/10`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <p className="text-sm text-gray-400 mb-2">{title}</p>
      <p className="text-3xl font-bold text-white mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
};

// Day Card Component
const DayCard = ({ dayData, onClick }) => {
  return (
    <div 
      className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-800 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:border-blue-500/30"
      onClick={onClick}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/10 p-2 rounded-xl">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">
              {new Date(dayData.date).toLocaleDateString('ar-EG', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              })}
            </h3>
          </div>
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-800/50 p-3 rounded-xl">
            <p className="text-xs text-gray-400 mb-1">المعاملات</p>
            <p className="text-xl font-bold text-white">{dayData.deposits_count}</p>
          </div>
          <div className="bg-gray-800/50 p-3 rounded-xl">
            <p className="text-xs text-gray-400 mb-1">المبلغ</p>
            <p className="text-xl font-bold text-green-400">${dayData.total_amount.toLocaleString()}</p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-gray-400">{dayData.payment_methods.length} طريقة دفع</span>
          <span className="text-blue-400 font-semibold">عرض التفاصيل ←</span>
        </div>
      </div>
    </div>
  );
};

// Main Component
const TransactionsReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  // Set default dates to last 7 days
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    return {
      start: sevenDaysAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  const handleDayClick = (day) => {
    navigate(`/transaction/day/${day.date}`);
  };

  // Remove useEffect - only fetch on initial load
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array - runs only once on mount

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://dailycard.co/api/dashboard/daily-transactions-summary/?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  

  // Handler for apply filter button
  const handleApplyFilter = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
          <p className="text-white text-lg">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Reverse the daily breakdown to show newest first
  const reversedDailyBreakdown = [...data.daily_breakdown].reverse();

  // Prepare chart data (reversed)
  const chartData = reversedDailyBreakdown.map((day) => ({
    date: new Date(day.date).toLocaleDateString("ar-EG", {
      month: "short",
      day: "numeric",
    }),
    amount: day.total_amount,
    count: day.deposits_count,
    commission: day.total_commission,
    paid: day.deposits_as_paid.amount,
    debt: day.deposits_as_debt.amount,
  }));

  // Payment methods aggregation
  const paymentMethodsData = {};
  reversedDailyBreakdown.forEach((day) => {
    day.payment_methods.forEach((method) => {
      if (!paymentMethodsData[method.method_name]) {
        paymentMethodsData[method.method_name] = {
          name: method.method_name,
          value: 0,
          count: 0,
        };
      }
      paymentMethodsData[method.method_name].value += method.total_amount;
      paymentMethodsData[method.method_name].count += method.count;
    });
  });

  const topPaymentMethods = Object.values(paymentMethodsData)
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  // Prepare payment methods over time data
  const paymentMethodsOverTime = reversedDailyBreakdown.map((day) => {
    const dayData = {
      date: new Date(day.date).toLocaleDateString("ar-EG", {
        month: "short",
        day: "numeric",
      }),
    };

    day.payment_methods.forEach((method) => {
      dayData[`${method.method_name}_amount`] = method.total_amount;
      dayData[`${method.method_name}_count`] = method.count;
    });

    return dayData;
  });

  // Prepare combined payment methods data for the big chart
  const combinedPaymentMethodsData = topPaymentMethods.map((method) => ({
    name: method.name,
    count: method.count,
    amount: method.value,
  }));

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
      dir="rtl"
    >
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 container mx-auto p-6">
        {/* Header with Actions */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/transaction")}
              className="p-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-4xl font-bold text-white">تقرير المعاملات</h1>
              <p className="text-gray-400 text-lg">
                من {new Date(startDate).toLocaleDateString('ar-EG')} إلى {new Date(endDate).toLocaleDateString('ar-EG')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">من تاريخ</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">إلى تاريخ</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchData}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>جاري التحميل...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    <span>تطبيق الفلتر</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

      {/* Grand Totals KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="إجمالي المعاملات"
            value={data.grand_totals.total_deposits_count.toLocaleString()}
            subtitle="عدد المعاملات الكلي"
            icon={FileText}
            color="blue"
          />
          <KPICard
            title="إجمالي المبلغ"
            value={`$${data.grand_totals.total_amount.toLocaleString()}`}
            subtitle="المبلغ الإجمالي"
            icon={DollarSign}
            color="green"
          />
          <KPICard
            title="إجمالي العمولات"
            value={`$${data.grand_totals.total_commission.toFixed(2)}`}
            subtitle="العمولات المحصلة"
            icon={Percent}
            color="orange"
          />
          <KPICard
            title="لإجمالي النهائي"
            value={`$${data.grand_totals.total_combined.toLocaleString()}`}
            subtitle="المبلغ + العمولة"
            icon={TrendingUp}
            color="purple"
          />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <h3 className="text-lg font-semibold text-white">
                المعاملات المدفوعة
              </h3>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {data.grand_totals.deposits_as_paid_count}
            </p>
            <p className="text-green-400 text-lg">
              ${data.grand_totals.deposits_as_paid_amount.toLocaleString()}
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-orange-400" />
              <h3 className="text-lg font-semibold text-white">
                المعاملات كدين
              </h3>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {data.grand_totals.deposits_as_debt_count}
            </p>
            <p className="text-orange-400 text-lg">
              ${data.grand_totals.deposits_as_debt_amount.toLocaleString()}
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <h3 className="text-lg font-semibold text-white">الاستثناءات</h3>
            </div>
            <p className="text-3xl font-bold text-red-400">
              ${data.grand_totals.total_exception_amount.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Amount Trend */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              تطور المبالغ اليومية
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="المبلغ الإجمالي"
                />
                <Line
                  type="monotone"
                  dataKey="paid"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="المدفوع"
                />
                <Line
                  type="monotone"
                  dataKey="debt"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="الدين"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Count Trend */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              عدد المعاملات اليومية
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend />
                <Bar
                  dataKey="count"
                  fill="#8b5cf6"
                  radius={[8, 8, 0, 0]}
                  name="عدد المعاملات"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Methods Analysis Section */}
        <div className="space-y-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-green-400" />
            تحليل طرق الدفع
          </h2>

          {/* Combined Payment Methods Chart - Count & Amount */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-400" />
              مقارنة طرق الدفع (العدد والمبلغ)
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={combinedPaymentMethodsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="name" 
                  stroke="#9ca3af"
                  angle={-45}
                  textAnchor="end"
                  height={120}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#9ca3af"
                  label={{ value: 'عدد المعاملات', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af"
                  label={{ value: 'المبلغ ($)', angle: 90, position: 'insideRight', fill: '#9ca3af' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151', 
                    borderRadius: '8px' 
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name) => {
                    if (name === 'عدد المعاملات') return [value, name];
                    return [`$${value.toLocaleString()}`, name];
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="count" 
                  fill="#8b5cf6" 
                  name="عدد المعاملات"
                  radius={[8, 8, 0, 0]}
                />
                <Bar 
                  yAxisId="right"
                  dataKey="amount" 
                  fill="#10b981" 
                  name="المبلغ الإجمالي"
                  radius={[8, 8, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Individual Payment Methods Over Time */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topPaymentMethods.map((method, index) => {
              // Get data for this specific method over time
              const methodOverTimeData = reversedDailyBreakdown.map((day) => {
                const methodData = day.payment_methods.find(
                  (m) => m.method_name === method.name
                );
                return {
                  date: new Date(day.date).toLocaleDateString("ar-EG", {
                    month: "short",
                    day: "numeric",
                  }),
                  amount: methodData?.total_amount || 0,
                  count: methodData?.count || 0,
                };
              });

              return (
                <div
                  key={method.name}
                  className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-800 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <h3 className="text-lg font-bold text-white">
                        {method.name}
                      </h3>
                    </div>
                    <div className="text-sm text-gray-400">
                      {method.count} معاملة
                    </div>
                  </div>

                  

                  {/* Chart */}
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={methodOverTimeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date"
                        stroke="#9ca3af"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        yAxisId="left"
                        stroke="#9ca3af"
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="#9ca3af"
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "1px solid #374151",
                          borderRadius: "8px",
                        }}
                        labelStyle={{ color: "#fff" }}
                        formatter={(value, name) => {
                          if (name === "عدد المعاملات") return [value, name];
                          return [`$${value.toLocaleString()}`, name];
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="right"
                        dataKey="amount"
                        fill={COLORS[index % COLORS.length]}
                        radius={[8, 8, 0, 0]}
                        name="المبلغ"
                        opacity={0.8}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="count"
                        stroke="#ef4444"
                        strokeWidth={3}
                        dot={{ fill: "#ef4444", r: 4 }}
                        name="عدد المعاملات"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>

          
        </div>

        {/* Daily Breakdown */}
        <div>
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-blue-400" />
          التفاصيل اليومية
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {reversedDailyBreakdown.map((day) => (
            <DayCard 
              key={day.date} 
              dayData={day} 
              onClick={() => handleDayClick(day)}
            />
          ))}
        </div>
      </div>

      </div>

      
    </div>
  );
};

export default TransactionsReport;
